import { QueryClient, QueryFunction } from "@tanstack/react-query";

/**
 * API Base Path
 * When deployed behind Nginx at /ooe/, all API calls need to be prefixed
 * so the browser sends them through the correct proxy location block.
 * In development (Vite dev server), no prefix is needed.
 */
const API_BASE = import.meta.env.PROD ? "/ooe" : "";

/**
 * Prepend the deployment base path to API URLs.
 * Only prefixes paths that start with "/" and don't already include the base.
 */
export function resolveUrl(url: string): string {
  if (API_BASE && url.startsWith("/") && !url.startsWith(API_BASE + "/")) {
    return API_BASE + url;
  }
  return url;
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const res = await fetch(resolveUrl(url), {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(resolveUrl(queryKey[0] as string), {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
